// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createHandlerChainRunner, Handler, HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { updateCart } from "@insite/client-framework/Services/CartService";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { Draft } from "immer";
import cloneDeep from "lodash/cloneDeep"

// step update handler
export const DispatchStepperState: Handler<{ step: number }> = props => {
    const step = props.parameter.step;
    props.dispatch({ type: "Checkout/Stepper", step } as any);
};
const xCheckoutStepperUpdate = createHandlerChainRunner([DispatchStepperState], "xCheckoutStepperUpdate");
export { xCheckoutStepperUpdate };

// ValidationStepperState
export const DispatchValidationStepper: Handler<{ nextButtonDisabled?: boolean }> = props => {
    const nextButtonDisabled = props.parameter.nextButtonDisabled ?? false;
    props.dispatch({ type: "Checkout/validationStepper", nextButtonDisabled } as any);
};
const xCheckoutValidationStepperUpdate = createHandlerChainRunner(
    [DispatchValidationStepper],
    "xCheckoutValidationStepperUpdate",
);
export { xCheckoutValidationStepperUpdate };

// Steps Total update handler
export const DispatchStepsTotalState: Handler<{ stepsTotal: number }> = props => {
    const stepsTotal = props.parameter.stepsTotal;
    props.dispatch({ type: "Checkout/stepsTotal", stepsTotal } as any);
};
const xCheckoutStepsTotalUpdate = createHandlerChainRunner([DispatchStepsTotalState], "xCheckoutStepsTotalUpdate");
export { xCheckoutStepsTotalUpdate };

// Card Info update handler
export const DispatchUpdateCardInfo: Handler<{ lastFour: string; cardType: string }> = props => {
    props.dispatch({
        type: "Checkout/updateCardInfo",
        lastFour: props.parameter.lastFour,
        cardType: props.parameter.cardType,
    } as any);
};
const xCheckoutCardInfoUpdate = createHandlerChainRunner([DispatchUpdateCardInfo], "xCheckoutCardInfoUpdate");
export { xCheckoutCardInfoUpdate };

// Card description update handler
export const DispatchUpdateCardDescription: Handler<{ description: string }> = props => {
    props.dispatch({
        type: "Checkout/updateCardDescription",
        description: props.parameter.description,
    } as any);
};
const xCheckoutCardDescriptionUpdate = createHandlerChainRunner(
    [DispatchUpdateCardDescription],
    "xCheckoutCardDescriptionUpdate",
);
export { xCheckoutCardDescriptionUpdate };

// expiration Date update handler
export const DispatchUpdateExpirationDate: Handler<{ expirationDate: string }> = props => {
    const expirationDate = props.parameter.expirationDate;
    props.dispatch({ type: "Checkout/updateExpirationDate", expirationDate } as any);
};

const xCheckoutExpirationDateUpdate = createHandlerChainRunner(
    [DispatchUpdateExpirationDate],
    "xCheckoutExpirationDateUpdate",
);
export { xCheckoutExpirationDateUpdate };

// poNumber update handler
export const DispatchUpdateCardPoNumber: Handler<{ poNumber: string }> = props => {
    props.dispatch({
        type: "Checkout/updatePoNumber",
        poNumber: props.parameter.poNumber,
    } as any);
};
const xCheckoutpoNumberUpdate = createHandlerChainRunner([DispatchUpdateCardPoNumber], "xCheckoutpoNumberUpdate");
export { xCheckoutpoNumberUpdate };

// job update handler
export const DispatchUpdateCardJob: Handler<{ job: string }> = props => {
    props.dispatch({
        type: "Checkout/updateJob",
        job: props.parameter.job,
    } as any);
};
const xCheckoutJobUpdate = createHandlerChainRunner([DispatchUpdateCardJob], "xCheckoutJobUpdate");
export { xCheckoutJobUpdate };

// job update handler
export const DispatchUpdatePaymentMethodName: Handler<{ paymentMethodName: string }> = props => {
    props.dispatch({
        type: "Checkout/updatePaymentMethodName",
        paymentMethodName: props.parameter.paymentMethodName,
    } as any);
};
const xCheckoutPaymentMethodNameUpdate = createHandlerChainRunner(
    [DispatchUpdatePaymentMethodName],
    "xCheckoutPaymentMethodNameUpdate",
);
export { xCheckoutPaymentMethodNameUpdate };

// ship complete update handler
type ShipCompleteParameter = { shipComplete: string, shouldUpdateCart?: boolean } & HasOnSuccess & HasOnError;
type ShipCompleteHandlerType = Handler<ShipCompleteParameter>;
export const DispatchUpdateShipComplete: ShipCompleteHandlerType = props => {
    props.dispatch({
        type: "Checkout/updateShipComplete",
        shipComplete: props.parameter.shipComplete,
    } as any);
};
export const UpdateCartSavingShipComplete: ShipCompleteHandlerType = async props => {
    if(!props.parameter.shouldUpdateCart) {
        return false;
    }
    const { value: cart } = getCurrentCartState(props.getState());
    
    if (!cart) {
        throw new Error("The cart is not loaded. Try reloading the page.");
    }

    const cartClone = cloneDeep(cart) as Draft<typeof cart>;
    cartClone.properties.customerShipComplete = props.parameter.shipComplete;
    await updateCart({ cart: cartClone });
};

export const ExecuteOnSuccessCallback: ShipCompleteHandlerType = props => {
    props.parameter.onSuccess?.();
};

export const ExecuteOnErrorCallback: ShipCompleteHandlerType = props => {
    props.parameter.onError?.();
};

const xCheckoutUpdateShipComplete = createHandlerChainRunner(
    [
        DispatchUpdateShipComplete,
        UpdateCartSavingShipComplete,
        ExecuteOnSuccessCallback,
        ExecuteOnErrorCallback,
    ],
    "xCheckoutUpdateShipComplete",
);
export { xCheckoutUpdateShipComplete };
